import { useEffect } from 'react';
import type { Feed, FeedEntry, UIComponent } from '~/services/layout/index.ts';
import {
  filterSkeletonEntries,
  generateSkeletonEntries,
  getLimit,
} from '~/services/layout/layout';
import { getIsPreferenceEditorByObserveStorageEndpointTag } from './preference-selector/use-preference-editor';

export function useFeedEntries({
  feedEntries,
  setFeedEntries,
  isLoading,
  isClient,
  feed,
  itemLimit,
  uiComponent,
  circularCondition,
  isClientStorage,
}: {
  feedEntries: FeedEntry[];
  setFeedEntries: any;
  isLoading?: boolean | undefined;
  isClient?: boolean | undefined;
  feed: Feed | undefined;
  itemLimit: number | undefined;
  uiComponent: UIComponent;
  circularCondition?: boolean;
  isClientStorage?: boolean;
}): FeedEntry[] {
  const feedEntriesLength = feed?.entry?.length || 0;

  itemLimit = getLimit(itemLimit);

  const loadingSkeletonEntriesLength: number | undefined =
    typeof itemLimit !== 'undefined' && itemLimit < 10 ? itemLimit : undefined;
  const generatedSkeletons = generateSkeletonEntries(
    loadingSkeletonEntriesLength
  );

  const isPreferenceEditorByObserveStorageEndpointTag: boolean =
    getIsPreferenceEditorByObserveStorageEndpointTag(uiComponent!);

  useEffect(() => {
    if (isPreferenceEditorByObserveStorageEndpointTag) return;

    const newEntries: FeedEntry[] | [] =
      isLoading && isClient
        ? generatedSkeletons
        : feed?.entry?.slice(0, itemLimit) || [];

    if (!isLoading || !isClient) {
      setFeedEntries(newEntries);
      return;
    }

    setFeedEntries((prevFeedEntries: any[]) => {
      return filterSkeletonEntries(prevFeedEntries);
    });
  }, [isLoading, feed]);

  if (isPreferenceEditorByObserveStorageEndpointTag) return feedEntries;

  if (feedEntriesLength === 0) return [];

  if (isClientStorage) return feed?.entry as FeedEntry[];

  const entries: FeedEntry[] | [] =
    isLoading && isClient
      ? generatedSkeletons
      : feed?.entry?.slice(0, itemLimit) || [];

  const res = feedEntries?.length > entries?.length ? feedEntries : entries;

  if (typeof circularCondition === 'undefined') return res;

  return circularCondition ? res : res.slice(0, itemLimit);
}

export function useFeedEntriesV1({
  initialFeedEntries,
  setFeedEntries,
}: {
  initialFeedEntries: FeedEntry[] | [];
  setFeedEntries: any;
}) {
  useEffect(() => {
    setFeedEntries(initialFeedEntries);
  }, []);
}
